import { c as _c } from "react/compiler-runtime";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import cn from "twsx";

// Heights follow the type and control scale: `xs`–`md` stand in for 12/13/15px
// text lines, `lg`/`xl` for 32/40px controls and rows, and the block sizes for
// panels, previews and whole-page placeholders. Width stays a call-site
// placement class.
const skeletonVariants = cva("animate-pulse bg-fill-hover motion-reduce:animate-none", {
  variants: {
    size: {
      xs: "h-3",
      sm: "h-4",
      md: "h-5",
      lg: "h-8",
      xl: "h-10",
      block: "h-24",
      panel: "h-44",
      page: "h-64",
      screen: "h-frame-stacked",
      fill: "h-full"
    },
    shape: {
      default: "rounded-md",
      pill: "rounded-full",
      square: "rounded-none"
    }
  },
  defaultVariants: {
    size: "sm",
    shape: "default"
  }
});
export type SkeletonProps = React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof skeletonVariants>;

/** Use when content is loading and its shape is known. */
function Skeleton(t0) {
  const $ = _c(12);
  let className;
  let props;
  let shape;
  let size;
  if ($[0] !== t0) {
    ({
      className,
      size,
      shape,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = shape;
    $[4] = size;
  } else {
    className = $[1];
    props = $[2];
    shape = $[3];
    size = $[4];
  }
  let t1;
  if ($[5] !== className || $[6] !== shape || $[7] !== size) {
    t1 = cn(skeletonVariants({
      size,
      shape
    }), className);
    $[5] = className;
    $[6] = shape;
    $[7] = size;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== props || $[10] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[9] = props;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}
Skeleton.displayName = "Skeleton";
export { Skeleton };